import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  DollarSign,
  Users,
  Clock,
  TrendingUp,
  LoaderPinwheel,
  RefreshCw,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Building2,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import Navbar from "../components/ui/Navbar";
import { hasPermission } from "../utils/permissions";

const API = process.env.REACT_APP_API_URL;
const COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
];

function fmt(n) {
  return (n || 0).toLocaleString("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  });
}
function fmtH(min) {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const MESES = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

export default function CostosClientePage() {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [clientes, setClientes] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [reporte, setReporte] = useState({
    registros: [],
    totales: {},
    porTecnico: [],
    porCliente: [],
    porTipo: [],
  });
  const [tendencia, setTendencia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPag, setTotalPag] = useState(1);
  const [sincronizando, setSincronizando] = useState(false);

  const [filtros, setFiltros] = useState({
    cliente: "",
    tipoOrigen: "",
    fechaDesde: "",
    fechaHasta: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, ...filtros };
      Object.keys(params).forEach((k) => {
        if (!params[k]) delete params[k];
      });

      const [rep, res, tend] = await Promise.all([
        axios.get(`${API}/costos-cliente`, { headers, params }),
        axios.get(`${API}/costos-cliente/resumen`, {
          headers,
          params: {
            fechaDesde: filtros.fechaDesde,
            fechaHasta: filtros.fechaHasta,
          },
        }),
        axios.get(`${API}/costos-cliente/tendencia`, {
          headers,
          params: { cliente: filtros.cliente, meses: 12 },
        }),
      ]);

      setReporte(rep.data);
      setTotalPag(rep.data.pages || 1);
      setResumen(res.data);
      setTendencia(
        tend.data.map((d) => ({
          nombre: `${MESES[d._id.month - 1]} ${d._id.year}`,
          total: d.total,
          manoObra: d.manoObra,
          repuestos: d.repuestos,
          servicios: d.servicios,
        })),
      );
    } catch (err) {
      toast.error("Error al cargar datos de costos");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [page, filtros, token]);

  useEffect(() => {
    axios
      .get(`${API}/clientes`, { headers })
      .then(({ data }) => setClientes(data))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFiltro = (e) => {
    setFiltros((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1);
  };

  const handleSincronizar = async () => {
    if (
      !window.confirm(
        "¿Sincronizar gestiones al ledger de costos? Esto puede tardar unos segundos.",
      )
    )
      return;
    setSincronizando(true);
    try {
      const { data } = await axios.post(
        `${API}/costos-cliente/sincronizar`,
        {},
        { headers },
      );
      toast.success(data.message);
      fetchData();
    } catch (err) {
      toast.error("Error al sincronizar");
    } finally {
      setSincronizando(false);
    }
  };

  const { totales, porTecnico, porTipo, registros } = reporte;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-600 rounded-xl">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Costos de Servicios
              </h1>
              <p className="text-sm text-gray-500">
                Análisis de costos por cliente, técnico y tipo de servicio
              </p>
            </div>
          </div>

          {hasPermission("costos_cliente.exportar") && (
            <button
              onClick={handleSincronizar}
              disabled={sincronizando}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition shadow disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${sincronizando ? "animate-spin" : ""}`}
              />
              {sincronizando ? "Sincronizando..." : "Sincronizar gestiones"}
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Cliente
              </label>
              <select
                name="cliente"
                value={filtros.cliente}
                onChange={handleFiltro}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Todos</option>
                {clientes.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Tipo origen
              </label>
              <select
                name="tipoOrigen"
                value={filtros.tipoOrigen}
                onChange={handleFiltro}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Todos</option>
                <option value="gestion">Gestión</option>
                <option value="soporte">Soporte</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Desde
              </label>
              <input
                type="date"
                name="fechaDesde"
                value={filtros.fechaDesde}
                onChange={handleFiltro}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Hasta
              </label>
              <input
                type="date"
                name="fechaHasta"
                value={filtros.fechaHasta}
                onChange={handleFiltro}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            color="green"
            icon={<DollarSign />}
            label="Costo total"
            value={fmt(totales.totalGeneral)}
          />
          <KpiCard
            color="blue"
            icon={<Users />}
            label="Mano de obra"
            value={fmt(totales.totalManoObra)}
          />
          <KpiCard
            color="orange"
            icon={<Clock />}
            label="Horas trabajadas"
            value={fmtH(totales.totalMinutos)}
          />
          <KpiCard
            color="purple"
            icon={<BarChart3 />}
            label="Servicios"
            value={totales.count || 0}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Tendencia mensual */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" /> Tendencia mensual
              (12 meses)
            </h3>
            {tendencia.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Sin datos de tendencia
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={tendencia}>
                  <XAxis dataKey="nombre" tick={{ fontSize: 10 }} />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Bar
                    dataKey="manoObra"
                    name="Mano de obra"
                    fill="#3B82F6"
                    stackId="a"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="repuestos"
                    name="Repuestos"
                    fill="#10B981"
                    stackId="a"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Por tipo */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Distribución por tipo
            </h3>
            {porTipo.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Sin datos
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={porTipo.map((p) => ({ name: p._id, value: p.total }))}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name }) => name}
                  >
                    {porTipo.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Por técnico */}
        {porTecnico.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-600" /> Costos por técnico
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold text-gray-600">
                      Técnico
                    </th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-600">
                      Tiempo
                    </th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-600">
                      Costo
                    </th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-600">
                      % del total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {porTecnico.map((t, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">
                        {t.nombre || "Desconocido"}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-600">
                        {fmtH(t.minutos)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-green-700">
                        {fmt(t.costo)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-500">
                        {totales.totalManoObra > 0
                          ? `${Math.round((t.costo / totales.totalManoObra) * 100)}%`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Por cliente */}
        {resumen.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" /> Resumen por
              cliente
            </h3>
            <div className="space-y-2">
              {resumen.map((c, i) => {
                const pct =
                  totales.totalGeneral > 0
                    ? (c.totalCosto / totales.totalGeneral) * 100
                    : 0;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-28 text-xs text-gray-600 truncate">
                      {c.nombre}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                    <div className="text-xs font-semibold text-gray-700 w-28 text-right">
                      {fmt(c.totalCosto)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tabla de registros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">
              Detalle de servicios
            </h3>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
              <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
              Cargando datos, por favor espera...
            </div>
          ) : registros.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              <DollarSign className="h-10 w-10 mx-auto mb-2 opacity-30" />
              Sin registros de costos. Sincroniza las gestiones para empezar.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Origen
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Cliente
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Técnico
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">
                      Mano obra
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">
                      Repuestos
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">
                      Total
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {registros.map((r) => (
                    <tr key={r._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800 text-xs">
                          {r.origenCodigo || r.origenId}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                              r.tipoOrigen === "soporte"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            {r.tipoOrigen}
                          </span>
                          <span className="text-xs text-gray-400">
                            {r.origenTipo}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-xs">
                        {r.cliente?.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {r.tecnicoPrincipal?.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 text-xs">
                        {fmt(r.costoManoObra)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 text-xs">
                        {fmt(r.costoRepuestos)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-700">
                        {fmt(r.costoTotal)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {new Date(r.fechaServicio).toLocaleDateString("es-CO")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPag > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-white transition"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-600">
                Página {page} de {totalPag}
              </span>
              <button
                disabled={page === totalPag}
                onClick={() => setPage((p) => p + 1)}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-white transition"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ color, icon, label, value }) {
  const colors = {
    green: "bg-green-50 text-green-700 border-green-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    orange: "bg-orange-50 text-orange-700 border-orange-100",
    purple: "bg-purple-50 text-purple-700 border-purple-100",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2 opacity-70">
        {React.cloneElement(icon, { className: "h-4 w-4" })}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}
